/* assets/variant-picker-airbnb.js
   - Hidden <variant-radios> と完全同期
   - 一致バリアントIDを product form に直書き
   - さらに価格等を section fetch で強制再描画（保険）
   - 選択カード左寄せスクロール / ラベル高さ安定
*/
(() => {
  const CSS_ESCAPE = (window.CSS && CSS.escape)
    ? (s) => CSS.escape(String(s))
    : (s) => String(s).replace(/["\\]/g, "\\$&");

  function initPicker(root) {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = "true";

    const radios = root.querySelector("#vpA-radios-" + root.dataset.sectionId);
    if (!radios) return;

    const variants = JSON.parse(root.dataset.variants || "[]");
    const fieldsets = Array.from(radios.querySelectorAll('fieldset.product-form__input, fieldset.js.product-form__input'));
    const totalOptions = fieldsets.length;

    // Dawn が参照する URL/section
    const productUrl = radios.getAttribute('data-url') || window.location.pathname;
    const sectionId  = radios.getAttribute('data-section') || root.dataset.sectionId;

    // ========= state helpers =========
    function getSelected() {
      const sel = {};
      fieldsets.forEach((fs, i) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        sel[i] = checked ? checked.value : null;
      });
      return sel;
    }

    function findMatchingVariant(sel) {
      return variants.find(v => {
        for (let i = 0; i < totalOptions; i++) {
          if (!sel[i] || v.options[i] !== sel[i]) return false;
        }
        return true;
      }) || null;
    }

    function isValueAvailable(index, value, sel) {
      return variants.some(v => {
        if (v.available === false) return false;
        for (let i = 0; i < totalOptions; i++) {
          const need = (i === index) ? value : sel[i];
          if (need && v.options[i] !== need) return false;
        }
        return true;
      });
    }

    // ========= UI sync =========
    function reflectToUI() {
      const sel = getSelected();
      root.querySelectorAll(".vpA-group").forEach(group => {
        const idx = Number(group.dataset.optionIndex);
        const selected = sel[idx];
        group.querySelectorAll('[data-value]').forEach(btn => {
          const on = btn.dataset.value === selected;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      });
    }

    function updateDisabledStates() {
      const sel = getSelected();
      root.querySelectorAll(".vpA-group").forEach(group => {
        const idx = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-value]').forEach(btn => {
          const ok = isValueAvailable(idx, btn.dataset.value, sel);
          btn.classList.toggle('is-disabled', !ok);
          btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        });
      });
    }

    function scrollItemIntoView(scroller, el, opts = { align: 'start' }) {
      const pad = 8;
      const elL = el.offsetLeft - pad;
      const elR = elL + el.offsetWidth + pad * 2;
      const viewL = scroller.scrollLeft;
      const viewR = viewL + scroller.clientWidth;
      let target = viewL;
      if (opts.align === 'center') {
        target = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
      } else {
        if (elL < viewL) target = elL;
        if (elR > viewR) target = elR - scroller.clientWidth;
      }
      try { scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' }); }
      catch (_) { scroller.scrollLeft = Math.max(0, target); }
    }

    // ========= 強制再描画（保険） =========
    async function renderProductInfoFallback(variantId) {
      try {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', sectionId);
        const html = await fetch(url.toString(), { credentials: 'same-origin' }).then(r => r.text());

        // 取得した section の中から price を優先的に差し替え
        const tpl = document.createElement('template');
        tpl.innerHTML = html;

        const newPrice =
          tpl.content.getElementById(`price-${sectionId}`) ||
          tpl.content.querySelector(`#ProductInfo-${sectionId} .price`) ||
          tpl.content.querySelector(`#MainProduct-${sectionId} .price`) ||
          tpl.content.querySelector('.product__info-wrapper .price') ||
          tpl.content.querySelector('.price');

        if (newPrice) {
          const curr =
            document.getElementById(`price-${sectionId}`) ||
            document.querySelector(`#ProductInfo-${sectionId} .price`) ||
            document.querySelector(`#MainProduct-${sectionId} .price`) ||
            document.querySelector('.product__info-wrapper .price') ||
            document.querySelector('.price');

          if (curr) curr.outerHTML = newPrice.outerHTML;
        }
      } catch (e) {
        // 失敗時は何もしない（Dawnの通常処理に任せる）
        console.debug('[vpA] render fallback failed', e);
      }
    }

    // ========= 選択コア =========
    function selectOption(index, value) {
      const fs = fieldsets[index];
      if (!fs) return;

      const sample = fs.querySelector('input[type="radio"]');
      if (!sample) return;
      const nameAttr = sample.getAttribute('name'); // options[用量] など
      const formId   = sample.getAttribute('form'); // product_form_id

      // 1) hidden ラジオに反映（Dawn へ通知）
      const target = radios.querySelector(
        `input[type="radio"][name="${CSS_ESCAPE(nameAttr)}"][value="${CSS_ESCAPE(value)}"]`
      );
      if (target && !target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        target.dispatchEvent(new Event('input',  { bubbles: true }));
      }

      // 2) 一致バリアントを特定してフォームへ直書き（確実に価格更新させる）
      const sel = getSelected();
      sel[index] = value;
      const variant = findMatchingVariant(sel);

      if (formId) {
        const formEl = document.getElementById(formId);
        if (formEl) {
          const idInput = formEl.querySelector('input[name="id"]');
          if (idInput && variant) {
            const prev = idInput.value;
            idInput.value = String(variant.id);
            if (prev !== idInput.value) {
              idInput.dispatchEvent(new Event('change', { bubbles: true }));
              idInput.dispatchEvent(new Event('input',  { bubbles: true }));
            }
          }
        }
      }

      // 3) URL ?variant= を更新
      if (variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        // 4) 保険：セクションの価格をサーバから取得・差し替え
        renderProductInfoFallback(variant.id);
      }

      // 5) UI/スクロール/無効化/高さ
      reflectToUI();
      updateDisabledStates();

      const group = root.querySelector(`.vpA-group[data-option-index="${index}"]`);
      if (group) {
        const scroller = group.querySelector('.scroller');
        const selectedBtn = group.querySelector(`[data-value="${CSS_ESCAPE(value)}"]`);
        if (scroller && selectedBtn) scrollItemIntoView(scroller, selectedBtn, { align: 'start' });
      }
      measureSoon(root);
    }

    // ========= ラベル高さ安定 =========
    function labelExceedsTwoLines(label) {
      const cs = getComputedStyle(label);
      const w = label.getBoundingClientRect().width;
      let lh = parseFloat(cs.lineHeight);
      if (isNaN(lh)) {
        const fs = parseFloat(cs.fontSize) || 16;
        lh = fs * 1.25;
      }
      const probe = document.createElement('div');
      probe.textContent = label.textContent;
      Object.assign(probe.style, {
        position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
        left: '-9999px', top: '0', whiteSpace: 'normal', wordBreak: 'break-word',
        width: w + 'px', fontFamily: cs.fontFamily, fontWeight: cs.fontWeight,
        fontSize: cs.fontSize, lineHeight: lh + 'px'
      });
      document.body.appendChild(probe);
      const lines = Math.round(probe.scrollHeight / lh);
      document.body.removeChild(probe);
      return lines > 2;
    }

    function balanceGroupHeights(rootEl) {
      const groups = rootEl.querySelectorAll('.vpA-group[data-type="card-frequency"], .vpA-group[data-type="card-image"]');
      groups.forEach(group => {
        let anyLong = false;
        group.querySelectorAll('.vpA-card__label').forEach(label => {
          if (labelExceedsTwoLines(label)) anyLong = true;
        });
        group.classList.toggle('group-lines-3', anyLong);
      });
    }

    const afterNextFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
    function measureSoon(rootEl) {
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => afterNextFrame(() => balanceGroupHeights(rootEl)));
      } else {
        afterNextFrame(() => balanceGroupHeights(rootEl));
      }
    }

    // ========= イベント =========
    // カスタムUI → 選択
    root.querySelectorAll('.vpA-group [data-value]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const group = e.currentTarget.closest('.vpA-group');
        const idx = Number(group.dataset.optionIndex);
        selectOption(idx, e.currentTarget.dataset.value);
      });
    });

    // hidden ラジオの変化にも追従
    radios.addEventListener('change', () => {
      reflectToUI();
      updateDisabledStates();
      root.querySelectorAll('.vpA-group').forEach(group => {
        const sel = group.querySelector('[data-value].is-selected');
        const scroller = group.querySelector('.scroller');
        if (sel && scroller) scrollItemIntoView(scroller, sel, { align: 'start' });
      });
      measureSoon(root);
    });

    // 初期同期
    reflectToUI();
    updateDisabledStates();
    measureSoon(root);
  }

  function boot() {
    document.querySelectorAll('[id^="variant-picker-airbnb-"]').forEach(initPicker);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
  document.addEventListener('shopify:section:select', boot);

  // 画面幅変更でも安定計測（デバウンス）
  let _vhTimer;
  window.addEventListener('resize', () => {
    clearTimeout(_vhTimer);
    _vhTimer = setTimeout(() => {
      document.querySelectorAll('[id^="variant-picker-airbnb-"]').forEach(el => {
        if (el.offsetParent !== null) {
          const radios = el.querySelector('variant-radios');
          if (radios) radios.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }, 120);
  });
})();
